/**
 * 客户详情页 Store
 * @description 将 useCustomerInfoDetail (unstated-next) 迁移到 zustand + immer
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { message } from 'antd'
import {
  getCustomerInfo,
  UpdateCustomer,
  getBindingRecords,
  getCustomerContactInfoList,
  addFollowRecord,
  addSdrFollowRecord,
} from '@services/customer'
import { getTaskListByCidWithCheck } from '@services/task'
import { addOpportunity, getOpportunityListByCidWithCheck, updateOpportunity } from '@services/sale-opportunity'
import { updateLeads, getTouchStatus } from '@services/lead'
import { globalStore, sessionStore } from '@store'
import { getNextCustomerId } from '@pages/customer/detail/utils'
import { isArray, isEmptyWithCustom, isEqualArray, parseCustomerExtend, isNullOrUndefined, getResList } from '@library'
import type {
  CustomerInfo,
  ContactInfoItem,
  TaskListDetail,
  UpdateCustomerFilter,
  AddOpportunityFilter,
  UpdateOpportunityFilter,
  SaleOpportunityItem,
  BindingRecordItem,
  TouchStatus,
  FollowRecordPayload,
  SdrFollowRecordPayload,
} from '@types'
import {
  ContactInfoStatusTypeEnum,
  CustomerStatusEnum,
  TaskStatusEnum,
  OpportunityStatusEnum,
  sortTypeEnum,
} from '@types'

// 客户任务拉取结果状态：用于区分「确实没有未处理任务」与「尚未加载 / 加载失败」。
// listUnhandled 为空数组时这三种情形无法区分，提交跟进记录时必须依赖本状态判断。
export type TaskFetchState = 'idle' | 'loading' | 'success' | 'error'

// ========== State 接口定义 ==========
interface CustomerDetailState {
  // ====== 初始化参数 ======
  customerId: number
  queue: number | undefined
  search: string // window.location.search，用于 gotoNextCustomer

  // ====== 客户基本信息 ======
  customerInfoLoading: boolean
  customerInfo: CustomerInfo | undefined
  customerExtendInfo: any
  cid: string
  status: number | undefined
  readonly: boolean
  isFollower: boolean

  // ====== 派生权限状态 ======
  canFollow: boolean
  canFollowSdr: boolean
  canContactCustomer: boolean
  saleOpportunityCanComment: boolean
  saleOpportunityCanEdit: boolean
  showNext: boolean

  // ====== 联系人信息 ======
  contactLoading: boolean
  contactInfoList: ContactInfoItem[]
  recommendedContact: ContactInfoItem | undefined

  // ====== 任务/线索 ======
  taskLoading: boolean
  /** 任务拉取结果状态，用于区分「无未处理任务」与「未加载 / 加载失败」 */
  taskFetchState: TaskFetchState
  taskTotal: number
  listUnhandled: TaskListDetail[]
  listHandled: TaskListDetail[]
  listClosed: TaskListDetail[]
  selectedTaskIds: number[]

  // ====== 销售机会 ======
  saleOpportunityLoading: boolean
  saleOpportunityList: SaleOpportunityItem[]
  saleOpportunityTotal: number
  saleOpportunityProcessingTotal: number

  // ====== 绑定记录 ======
  bindingRecordsLoading: boolean
  bindingRecordsList: BindingRecordItem[]
  bindingRecordsError: string | undefined

  // ====== 触达状态 ======
  touchStatusList: TouchStatus[]

  /**
   * 跟进记录刷新信号：每次通过 addFollowRecordAndRefresh 成功保存跟进记录后自增。
   * 历史跟进记录列表（数据未收敛到本 store，由 useTableService 自管）据此重新拉取。
   */
  followRecordVersion: number

  // ====== Actions ======
  init: (params: { customerId: number; queue?: number; search?: string }) => void
  reset: () => void

  // 数据获取
  /**
   * 拉取客户基本信息。
   * @param options.withRelated 是否连带拉取依赖 cid 的子数据（联系人/任务/商机/绑定记录），默认 true。
   *   保存跟进记录后的局部刷新只需要客户信息本身，可传 false 精简请求。
   */
  fetchCustomerInfo: (options?: { withRelated?: boolean }) => Promise<void>
  fetchContactInfoList: () => Promise<void>
  fetchTaskList: () => Promise<void>
  fetchSaleOpportunityList: () => Promise<void>
  fetchBindingRecords: () => Promise<void>
  fetchTouchStatus: () => Promise<void>

  // 业务操作（数据变更）
  updateCustomerTag: (tags: number[], type: 'public' | 'private') => Promise<void>
  updateCustomerItScale: (itScale: number) => Promise<void>
  updateLeadTag: (task: TaskListDetail, tags: number[], type: 'public' | 'private') => Promise<void>
  /**
   * 保存跟进记录并局部刷新客户详情页（与 addSaleOpportunity 保持一致的形式）：
   * 成功后刷新客户信息（下次跟进时间）、任务列表（任务状态），并递增跟进记录刷新信号。
   * @returns 出错返回 true，成功返回 undefined
   */
  addFollowRecordAndRefresh: (payload: FollowRecordPayload) => Promise<boolean | undefined>
  /**
   * 保存 SDR 跟进记录并局部刷新（SDR 业务线走 AddSDRFollowRecord 接口，与上者仅接口不同）。
   * @returns 出错返回 true，成功返回 undefined
   */
  addSdrFollowRecordAndRefresh: (payload: SdrFollowRecordPayload) => Promise<boolean | undefined>
  addSaleOpportunity: (payload: AddOpportunityFilter, successMsg?: string) => Promise<boolean | undefined>
  updateSaleOpportunity: (payload: UpdateOpportunityFilter) => Promise<boolean | undefined>
  gotoNextCustomer: () => Promise<void>

  // Setter
  setSelectedTaskIds: (updater: number[] | ((prev: number[]) => number[])) => void
}

// ========== 初始状态 ==========
const initialState = {
  customerId: 0,
  queue: undefined as number | undefined,
  search: '',

  customerInfoLoading: false,
  customerInfo: undefined as CustomerInfo | undefined,
  customerExtendInfo: {} as any,
  cid: '',
  status: undefined as number | undefined,
  readonly: false,
  isFollower: false,

  canFollow: false,
  canFollowSdr: false,
  canContactCustomer: true,
  saleOpportunityCanComment: false,
  saleOpportunityCanEdit: false,
  showNext: false,

  contactLoading: false,
  contactInfoList: [] as ContactInfoItem[],
  recommendedContact: undefined as ContactInfoItem | undefined,

  taskLoading: false,
  taskFetchState: 'idle' as TaskFetchState,
  taskTotal: 0,
  listUnhandled: [] as TaskListDetail[],
  listHandled: [] as TaskListDetail[],
  listClosed: [] as TaskListDetail[],
  selectedTaskIds: [] as number[],

  saleOpportunityLoading: false,
  saleOpportunityList: [] as SaleOpportunityItem[],
  saleOpportunityTotal: 0,
  saleOpportunityProcessingTotal: 0,

  bindingRecordsLoading: false,
  bindingRecordsList: [] as BindingRecordItem[],
  bindingRecordsError: undefined as string | undefined,

  touchStatusList: [] as TouchStatus[],
}

// ========== 创建 Store ==========
export const customerDetailStore = create<CustomerDetailState>()(
  immer((set, get) => ({
    ...initialState,

    // 跟进记录刷新信号：作为「单调递增的一次性事件计数」，故意不纳入 initialState，
    // reset() 时保留累计值。否则跨客户切换（详情页卸载触发 reset）会把它归零，
    // 导致不同客户先后各保存一次时信号值重复，列表 key 不变而无法刷新。
    followRecordVersion: 0,

    // ====== 初始化 ======
    init: ({ customerId, queue, search }) => {
      set(state => {
        state.customerId = customerId
        state.queue = queue
        state.search = search || ''
        state.showNext = !isEmptyWithCustom(queue)
      })
      get().fetchCustomerInfo()
      get().fetchTouchStatus()
    },

    reset: () => set(() => ({ ...initialState })),

    // ====== 数据获取 ======
    fetchCustomerInfo: async (options) => {
      const withRelated = options?.withRelated ?? true
      set(state => { state.customerInfoLoading = true })

      const [customerInfo, err] = await getCustomerInfo(get().customerId)
      if (err) {
        message.error('获取客户信息失败')
        set(state => { state.customerInfoLoading = false })
        return
      }

      const userInfo = sessionStore.getState().userInfo
      const authPointMap = globalStore.getState().authPointMap

      // 第一层派生：仅依赖 customerInfo
      const cid = customerInfo?.cid ?? ''
      const status = customerInfo?.status
      const readonly = customerInfo?.status === CustomerStatusEnum.TRANFSFER_OUT
      const customerExtendInfo = parseCustomerExtend(customerInfo?.extend)

      // 第二层派生：依赖第一层 + 外部 store
      const isFollower = customerInfo?.follower === userInfo?.salesName
      const canFollow = readonly ? false : (authPointMap.addFollowRecord?.result ? true : isFollower)
      const canFollowSdr = authPointMap.addFollowRecordSdrPermission?.result ? true : isFollower
      const canContactCustomer = !readonly
      const saleOpportunityCanComment = !!authPointMap.commentOpportunity?.result
      const saleOpportunityCanEdit = readonly ? false : (authPointMap.addFollowRecord?.result ? true : isFollower)

      set(state => {
        state.customerInfoLoading = false
        state.customerInfo = customerInfo
        state.customerExtendInfo = customerExtendInfo
        state.cid = cid
        state.status = status
        state.readonly = readonly
        state.isFollower = isFollower
        state.canFollow = canFollow
        state.canFollowSdr = canFollowSdr
        state.canContactCustomer = canContactCustomer
        state.saleOpportunityCanComment = saleOpportunityCanComment
        state.saleOpportunityCanEdit = saleOpportunityCanEdit
      })

      // cid 获取成功后，并行触发依赖 cid 的子请求
      // withRelated=false 时跳过（如保存跟进记录后的局部刷新，只需要客户信息本身）
      if (cid && withRelated) {
        get().fetchContactInfoList()
        get().fetchTaskList()
        get().fetchSaleOpportunityList()
        get().fetchBindingRecords()
      }
    },

    fetchContactInfoList: async () => {
      const cid = get().cid
      if (!cid) {
        set(state => { state.contactInfoList = []; state.recommendedContact = undefined })
        return
      }
      set(state => { state.contactLoading = true })
      const [res, err] = await getCustomerContactInfoList({ cid })
      set(state => { state.contactLoading = false })
      if (err) {
        message.error('获取联系人信息失败' + err.message)
        return
      }
      let list: ContactInfoItem[] = [...(res?.list || [])]
      const recommendedContact = list.find(item => item.status === ContactInfoStatusTypeEnum.RECOMMENDED)
      // 按状态排序：优先推荐、有效、未验证、无效、拒绝联系
      list = [
        ...list.filter(item => item.status === ContactInfoStatusTypeEnum.RECOMMENDED),
        ...list.filter(item => item.status === ContactInfoStatusTypeEnum.VALID),
        ...list.filter(item => item.status === ContactInfoStatusTypeEnum.NOT_VERIFIED),
        ...list.filter(item => item.status === ContactInfoStatusTypeEnum.INVALID),
        ...list.filter(item => item.status === ContactInfoStatusTypeEnum.BLACKLIST),
      ]
      set(state => {
        state.contactInfoList = list
        state.recommendedContact = recommendedContact
      })
    },

    fetchTaskList: async () => {
      const cid = get().cid
      if (!cid) return
      set(state => {
        state.taskLoading = true
        state.taskFetchState = 'loading'
      })
      const [res, err] = await getTaskListByCidWithCheck({ cid })
      if (err) {
        message.error('获取客户任务失败')
        set(state => {
          state.taskLoading = false
          state.taskFetchState = 'error'
        })
        return
      }
      const list = res?.list ?? []
      const total = res?.total ?? 0
      const listUnhandled = list.filter(item => item.status === TaskStatusEnum.UNHANDLED)
      const listHandled = list.filter(item => item.status === TaskStatusEnum.HANDLED)
      const listClosed = list.filter(item => item.status === TaskStatusEnum.CLOSED)
      // 默认勾选未处理任务：<=200全选，>200选前200条
      const unhandledTaskIds = listUnhandled.map(item => item.taskId)
      const selectedTaskIds = unhandledTaskIds.length > 200 ? unhandledTaskIds.slice(0, 200) : unhandledTaskIds

      set(state => {
        state.taskLoading = false
        state.taskFetchState = 'success'
        state.taskTotal = total
        state.listUnhandled = listUnhandled
        state.listHandled = listHandled
        state.listClosed = listClosed
        state.selectedTaskIds = selectedTaskIds
      })
    },

    fetchTouchStatus: async () => {
      const [res, err] = await getTouchStatus()
      if (err) {
        message.error('获取触达状态列表失败')
        return
      }
      set(state => { state.touchStatusList = res?.list ?? [] })
    },

    fetchSaleOpportunityList: async () => {
      const cid = get().cid
      if (!cid) return
      set(state => { state.saleOpportunityLoading = true })
      const [res, err] = await getOpportunityListByCidWithCheck({ cid, sort: 'createTime', sortType: sortTypeEnum.DESC })
      if (err) {
        message.error('获取客户销售机会失败')
        set(state => { state.saleOpportunityLoading = false })
        return
      }
      // 兼容 SaleOpportunityItem[] 和 CommonList<SaleOpportunityItem> 两种返回格式
      const [list] = getResList(res, [])
      set(state => {
        state.saleOpportunityLoading = false
        state.saleOpportunityList = list
        state.saleOpportunityTotal = list.length
        state.saleOpportunityProcessingTotal = list.filter(item => item.status === OpportunityStatusEnum.PROCESSING).length
      })
    },

    fetchBindingRecords: async () => {
      const cid = get().cid
      if (!cid) return
      set(state => { state.bindingRecordsLoading = true; state.bindingRecordsError = undefined })
      const [res, err] = await getBindingRecords({ cid: [cid] })
      set(state => {
        state.bindingRecordsLoading = false
        state.bindingRecordsList = res?.list ?? []
        state.bindingRecordsError = err?.message
      })
    },

    // ====== 业务操作（数据变更） ======
    updateCustomerTag: async (tags, type) => {
      const { cid, customerInfo } = get()
      if (!isArray(tags)) return
      if (isEqualArray(tags, customerInfo?.[`${type}Tags`]?.map?.(tag => tag[`${type}TagId`]))) return

      const payload: UpdateCustomerFilter = {
        filter: { cid: cid && [cid] },
        updateResult: {
          [`updateCustomer${type.charAt(0).toUpperCase() + type.slice(1)}Tags`]: {
            [`${type}TagIds`]: tags,
          },
        },
      }
      const [, err] = await UpdateCustomer(payload)
      if (err) {
        message.error(`打标失败${err.message}`)
        return
      }
      message.success('打标成功')
      get().fetchCustomerInfo()
    },

    updateCustomerItScale: async (itScale) => {
      const { cid } = get()
      const payload: UpdateCustomerFilter = {
        filter: { cid: cid && [cid] },
        updateResult: { itScale },
      }
      const [, err] = await UpdateCustomer(payload)
      if (err) {
        message.error(`更新IT规模失败${err.message}`)
        return
      }
      message.success('更新IT规模成功')
      get().fetchCustomerInfo()
    },

    updateLeadTag: async (task, tags, type) => {
      const { leadInfo } = task
      if (!isArray(tags)) return
      if (isEqualArray(tags, leadInfo?.[`${type}Tags`]?.map?.(tag => tag[`${type}TagId`]))) {
        message.warn('标签未改变')
        return
      }
      const [, err] = await updateLeads({
        filter: { leadIds: [leadInfo?.leadId] },
        updateResult: {
          [type === 'private' ? 'updateLeadPrivateTags' : 'updateLeadPublicTags']: {
            [`${type}TagIds`]: tags,
          },
        },
      })
      if (err) {
        message.error(`修改失败(${err.message})`)
        return
      }
      message.success('修改成功')
      get().fetchTaskList()
    },

    addFollowRecordAndRefresh: async (payload) => {
      const [, err] = await addFollowRecord(payload)
      if (err) {
        message.error(`保存失败(${err.message})`)
        return true
      }
      message.success('跟进记录保存成功')
      // 局部刷新：客户信息（下次跟进时间）+ 任务列表（任务状态由未处理转已处理）。
      // withRelated=false 避免连带重拉联系人/商机/绑定记录。
      await get().fetchCustomerInfo({ withRelated: false })
      await get().fetchTaskList()
      // 递增信号，通知历史跟进记录列表重新拉取（其数据由组件内 useTableService 自管）
      set(state => { state.followRecordVersion += 1 })
    },

    addSdrFollowRecordAndRefresh: async (payload) => {
      const [, err] = await addSdrFollowRecord(payload)
      if (err) {
        message.error(`保存失败(${err.message})`)
        return true
      }
      message.success('跟进记录保存成功')
      await get().fetchCustomerInfo({ withRelated: false })
      await get().fetchTaskList()
      set(state => { state.followRecordVersion += 1 })
    },

    addSaleOpportunity: async (payload, successMsg = '操作成功') => {
      const [, err] = await addOpportunity(payload)
      if (err) {
        message.error(`操作失败${err.message}`)
        return true
      }
      message.success(successMsg)
      get().fetchSaleOpportunityList()
    },

    updateSaleOpportunity: async (payload) => {
      const [, err] = await updateOpportunity(payload)
      if (err) {
        message.error(`操作失败${err.message}`)
        return true
      }
      message.success('操作成功')
      get().fetchSaleOpportunityList()
    },

    gotoNextCustomer: async () => {
      const { queue, customerId, search } = get()
      const nextId = await getNextCustomerId(queue, customerId)
      if (isNullOrUndefined(nextId)) {
        await message.info('已跟进完所勾选或所筛选的客户')
      } else {
        message.success('加载下一个客户...')
        window.open(`/customer/${nextId}${search}`, '_self')
      }
    },

    // ====== Setter ======
    setSelectedTaskIds: (updater) => {
      set(state => {
        state.selectedTaskIds = typeof updater === 'function' ? updater(state.selectedTaskIds) : updater
      })
    },
  }))
)
